import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from '../../firebase';

function TargetReportComp() {
  const db = getFirestore(app);
  const monthlyTarget = 730800;
  const [courseStats, setCourseStats] = useState([]);
  const [totalNeededStudents, setTotalNeededStudents] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const coursesSnapshot = await getDocs(collection(db, 'courses'));

      const courseMap = {};

      // Initialize courseMap
      coursesSnapshot.forEach(doc => {
        const course = doc.data();
        const name = course.mcourseName;
        const price = parseInt(course.feeDeposit || '0');

        if (name && price > 0) {
          courseMap[name] = {
            name,
            fee: price,
            ongoing: 0,
            completed: 0,
            deferred: 0,
            dropped: 0,
          };
        }
      });

      // Categorize users by course and status
      usersSnapshot.forEach(doc => {
        const user = doc.data();
        const courseName = user.course;
        const status = user.userStatus;

        if (courseMap[courseName]) {
          switch (status) {
            case 'Student':
              courseMap[courseName].ongoing += 1;
              break;
            case 'Completed':
              courseMap[courseName].completed += 1;
              break;
            case 'Deferred':
              courseMap[courseName].deferred += 1;
              break;
            case 'Dropped':
              courseMap[courseName].dropped += 1;
              break;
            default:
              break;
          }
        }
      });

      const topCourses = Object.values(courseMap)
        .sort((a, b) => b.ongoing - a.ongoing)
        .slice(0, 6);

      // 1. Calculate churn-adjusted weights
      let weightSum = 0;
      const weightedCourses = topCourses.map(course => {
        const total = course.ongoing + course.completed + course.deferred + course.dropped;
        const churnRate = total > 0 ? (course.deferred + course.dropped) / total : 0;
        const churnMultiplier = churnRate < 1 ? 1 / (1 - churnRate) : 1;

        const weight = course.fee * churnMultiplier;
        weightSum += weight;

        return {
          ...course,
          churnRate,
          churnMultiplier,
          weight,
        };
      });

      // 2. Allocate expected income and needed students
      let finalTotalExpectedIncome = 0;
      let finalTotalNeededStudents = 0;

      const finalStats = weightedCourses.map(course => {
        const courseShare = course.weight / weightSum;
        const expectedIncome = Math.round(monthlyTarget * courseShare);
        const neededStudents = Math.ceil(expectedIncome / course.fee);

        finalTotalExpectedIncome += expectedIncome;
        finalTotalNeededStudents += neededStudents;

        return {
          ...course,
          expectedIncome,
          neededStudents,
        };
      });

      setCourseStats(finalStats);
      setTotalNeededStudents(finalTotalNeededStudents);
    };

    fetchData();
  }, [db]);

  return (
    <div className="teamsComponent">
      <h4 className="mb-3">Course Focus Recommendations</h4>
      <p><strong>Monthly Target:</strong> KES {monthlyTarget.toLocaleString()}</p>
      <p><strong>Total Needed Students:</strong> {totalNeededStudents}</p>

      <table className="table table-bordered mt-3">
        <thead>
          <tr>
            <th>Course</th>
            <th>Ongoing</th>
            <th>Completed</th>
            <th>Deferred</th>
            <th>Dropped</th>
            <th>Fee (KES)</th>
            <th>Needed Students</th>
            <th>Expected Income</th>
          </tr>
        </thead>
        <tbody>
          {courseStats.map((course, idx) => (
            <tr key={idx}>
              <td>{course.name}</td>
              <td>{course.ongoing}</td>
              <td>{course.completed}</td>
              <td>{course.deferred}</td>
              <td>{course.dropped}</td>
              <td>{course.fee.toLocaleString()}</td>
              <td>{course.neededStudents}</td>
              <td>{course.expectedIncome.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p>
        The "Needed Students" value is calculated based on the monthly income target distributed proportionally across courses according to their fees.
        It adjusts for student dropout rates (deferred + dropped) to ensure the target income is met even after accounting for expected churn.
      </p>
    </div>
  );
}

export default TargetReportComp;